import { motion, AnimatePresence } from 'framer-motion'
import { useState } from 'react'

interface Props {
  accentColor: string
  accentSoft: string
}

const FAQ_ITEMS = [
  {
    q: 'Como funciona o processo após a compra do Protocolo?',
    a: 'Após a compra você recebe um email com um formulário de investigação profunda. Em até 72 horas após o preenchimento você recebe seu Mapa de Revelações e sua meditação personalizada por email.',
  },
  {
    q: 'A meditação do Protocolo é igual para todo mundo?',
    a: 'Não. Cada meditação é criada individualmente com base nas suas respostas no formulário. Ela é gravada com intenção direcionada especificamente para o seu campo vibracional.',
  },
  {
    q: 'Por quantos dias escuto a meditação do Protocolo?',
    a: 'Por 21 dias consecutivos antes de dormir. Esse é o tempo necessário para o inconsciente começar a reconfigurar os padrões identificados.',
  },
  {
    q: 'Posso começar pelo Ciclo 1 e depois fazer os outros ciclos?',
    a: 'Sim. Cada ciclo é independente. Você começa pelo Ciclo 1 e decide se quer continuar para os próximos no seu tempo.',
  },
  {
    q: 'O Mapa de Cura dos 4 Elementos é genérico ou personalizado?',
    a: 'É completamente personalizado. Criado com base nas suas respostas e escrito especificamente para você.',
  },
  {
    q: 'Em quanto tempo recebo meu Mapa?',
    a: 'Em até 72 horas após o preenchimento do formulário.',
  },
  {
    q: 'Tenho garantia nos dois produtos?',
    a: 'Sim. Garantia incondicional de 7 dias nos dois produtos. Se sentir que não foi o que esperava basta me enviar uma mensagem e devolvo 100% do investimento. Sem perguntas. Sem burocracia.',
  },
]

function FaqSection({ accentColor, accentSoft }: Props) {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  function toggle(i: number) {
    setOpenIndex(prev => (prev === i ? null : i))
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, delay: 0.2 }}
      className="mt-16 md:mt-20"
    >
      {/* Decorative divider */}
      <div
        className="h-px w-24 mx-auto mb-10"
        style={{
          background: `linear-gradient(90deg, transparent, ${accentSoft}66, transparent)`,
        }}
      />

      {/* Header */}
      <div className="text-center max-w-xl mx-auto mb-10 px-2">
        <p
          className="text-[10px] tracking-[0.4em] uppercase font-body mb-5"
          style={{ color: accentSoft }}
        >
          Tire suas dúvidas
        </p>
        <h3 className="font-display type-section text-[var(--ink,#e8e0d5)]">
          Perguntas{' '}
          <em className="italic font-light" style={{ color: accentSoft }}>
            Frequentes
          </em>
        </h3>
      </div>

      {/* Accordion */}
      <div className="max-w-2xl mx-auto flex flex-col gap-2">
        {FAQ_ITEMS.map((item, i) => {
          const isOpen = openIndex === i
          return (
            <div
              key={i}
              className="rounded-sm overflow-hidden transition-all duration-300"
              style={{
                background: isOpen
                  ? 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)'
                  : 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)',
                border: `1px solid ${isOpen ? accentSoft + '44' : 'rgba(255,255,255,0.07)'}`,
              }}
            >
              {/* Question row */}
              <button
                type="button"
                onClick={() => toggle(i)}
                aria-expanded={isOpen}
                className="w-full flex items-center justify-between gap-4 px-5 py-4 sm:px-6 sm:py-5 text-left focus-visible:outline-none"
              >
                <span
                  className="font-body type-body font-medium leading-snug"
                  style={{ color: isOpen ? 'var(--ink,#e8e0d5)' : 'rgba(232,224,213,0.85)' }}
                >
                  {item.q}
                </span>

                {/* Icon */}
                <span
                  className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-300"
                  style={{
                    background: isOpen ? `${accentColor}22` : 'rgba(255,255,255,0.05)',
                    border: `1px solid ${isOpen ? accentSoft + '55' : 'rgba(255,255,255,0.1)'}`,
                  }}
                  aria-hidden="true"
                >
                  <motion.svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={isOpen ? accentSoft : 'rgba(232,224,213,0.45)'}
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="w-3.5 h-3.5"
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <path d="M6 9l6 6 6-6" />
                  </motion.svg>
                </span>
              </button>

              {/* Answer */}
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    key="answer"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    style={{ overflow: 'hidden' }}
                  >
                    <div
                      className="px-5 pb-5 sm:px-6 sm:pb-6"
                      style={{ borderTop: `1px solid ${accentSoft}1a` }}
                    >
                      <p
                        className="font-body type-body pt-4"
                        style={{ color: 'rgba(232,224,213,0.70)', lineHeight: 1.75 }}
                      >
                        {item.a}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )
        })}
      </div>
    </motion.div>
  )
}

export default FaqSection
